import { useCallback, useEffect, useState } from "react";
import { Badge, Card, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  AdminActions,
  AdminButton,
  AdminListCard,
  AdminListMain,
  ConfirmDeleteButton,
} from "../../SuperAdmin/SharedComponents/ListView";
import { contractApi } from "../API/ContractAPI";
import ContractEditorModal from "./ContractEditorModal";
import ContractLogModal from "./ContractLogModal";

/**
 * The contracts of one company for one audience, with their acceptance counts and logs.
 * canManage decides whether add / edit / delete are shown; the server enforces the same
 * rule independently.
 */
export default function ContractSection({ companyId, audience, canManage, title, description }) {
  const { t } = useTranslation();
  const token = useSelector((s) => s.auth.user?.token);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // { contract: null } to add, { contract } to edit
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [logContractId, setLogContractId] = useState(null);

  const load = useCallback(() => {
    if (!companyId) return;
    setLoading(true);
    contractApi.list(token, companyId, audience).then((r) => {
      setLoading(false);
      if (r.ok) {
        setItems(r.data || []);
        setError(null);
      } else {
        setError(r.message || t("contractLoadFailed"));
      }
    });
  }, [token, companyId, audience, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openEditor = (contract) => {
    setSaveError(null);
    setEditing({ contract });
  };

  const save = ({ name, content }) => {
    setSaving(true);
    setSaveError(null);
    contractApi
      .upsert(token, { id: editing?.contract?.id ?? 0, companyId, audience, name, content })
      .then((r) => {
        setSaving(false);
        if (r.ok) {
          setEditing(null);
          load();
        } else {
          setSaveError(r.message || t("contractLoadFailed"));
        }
      });
  };

  const remove = (id) =>
    contractApi.remove(token, id).then((r) => {
      if (r.ok) load();
      else setError(r.message);
    });

  return (
    <Card className="default-card contract-section">
      <div className="contract-section__head">
        <div>
          <h5 className="contract-section__title">{title}</h5>
          {description && <p className="contract-section__desc">{description}</p>}
        </div>
        {canManage && <AdminButton onClick={() => openEditor(null)}>+ {t("addContract")}</AdminButton>}
      </div>

      {loading && items.length === 0 && (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {error && <div className="text-danger mb-2">{error}</div>}
      {!loading && !error && items.length === 0 && <p className="text-muted mb-0">{t("contractNoContracts")}</p>}

      {items.map((c) => {
        const allAccepted = c.totalCount > 0 && c.acceptedCount === c.totalCount;
        return (
          <AdminListCard key={c.id} className="mb-2">
            <AdminListMain>
              <span className="admin-list-title">{c.name}</span>
              <span className="admin-list-meta">
                {t("contractVersion")} {c.versionNumber}
              </span>
              <Badge bg={allAccepted ? "success" : "secondary"} className="tabular-nums">
                {t("contractAcceptedCount", { accepted: c.acceptedCount, total: c.totalCount })}
              </Badge>
            </AdminListMain>
            <AdminActions>
              <AdminButton variant="outline" onClick={() => setLogContractId(c.id)}>
                {t("contractLog")}
              </AdminButton>
              {canManage && (
                <>
                  <AdminButton variant="outline" onClick={() => openEditor(c)}>
                    {t("edit")}
                  </AdminButton>
                  <ConfirmDeleteButton message={t("contractDeleteConfirm")} onConfirm={() => remove(c.id)} />
                </>
              )}
            </AdminActions>
          </AdminListCard>
        );
      })}

      <ContractEditorModal
        show={!!editing}
        contract={editing?.contract || null}
        saving={saving}
        error={saveError}
        onHide={() => setEditing(null)}
        onSave={save}
      />
      <ContractLogModal
        show={!!logContractId}
        contractId={logContractId}
        token={token}
        onHide={() => {
          setLogContractId(null);
          load();
        }}
      />
    </Card>
  );
}
